import { useEffect, useRef, useState } from 'react';
import { useToast } from '@/common-utils/hooks/use-toast';
import getTableFetchError from '../functions/getTableFetchError';
import Entity from '../components/renders/entity/Entity';
import ICONS from '@/common-utils/enums/icons';

/**
 * - 1st API Gateway timeout → toast + auto-retry once
 * - retry success → dismiss toast
 * - 2nd gateway timeout or ALB timeout → error toast (+ propose export when available)
 * - bad gateway / other errors → dedicated toasts
 *
 * @param {Object} error - The error object
 * @param {Function} refetch - The refetch function
 * @param {boolean} isFetching - The fetching state
 * @param {Function} setShowModalConfirmExportAfterTimeout - Opens propose-export confirmation
 * @param {boolean} hasExport - Whether the table supports export
 * @returns {boolean} isRetrying - Keep skeleton visible between first timeout and retry end
 */
const useTableTimeout = (error, refetch, isFetching, setShowModalConfirmExportAfterTimeout, hasExport = false) => {
    // Toast
    const { toast, dismiss } = useToast();

    // State
    const [isRetrying, setIsRetrying] = useState(false);

    // Refs
    const timeoutRetryRef = useRef(0);
    const retryToastIdRef = useRef(null);
    const prevIsFetchingRef = useRef(false);
    const retryGaveUpRef = useRef(false);

    // Error types
    const { isTimeoutApiGateway, isTimeoutALB, isBadGateway } = getTableFetchError(error);

    // New fetch → allow a fresh retry cycle
    if (isFetching && retryGaveUpRef.current) {
        retryGaveUpRef.current = false;
    }

    // First gateway timeout ends before the effect sets isRetrying
    if (
        !isFetching
        && error
        && isTimeoutApiGateway
        && timeoutRetryRef.current === 0
        && !isRetrying
        && !retryGaveUpRef.current
    ) {
        setIsRetrying(true);
    }

    // Functions
    const dismissRetryToast = () => {
        if (retryToastIdRef.current) {
            dismiss(retryToastIdRef.current);
            retryToastIdRef.current = null;
        }
    };

    // Effect
    useEffect(() => {
        const fetchingEnded = prevIsFetchingRef.current && !isFetching;

        // Retry succeeded
        if (fetchingEnded && !error && timeoutRetryRef.current === 1) {
            timeoutRetryRef.current = 0;
            setIsRetrying(false);
            dismissRetryToast();
        }
        // Fetch finished with an error
        else if (fetchingEnded && error) {
            // Case 1: API Gateway timeout
            if (isTimeoutApiGateway && timeoutRetryRef.current === 0) {
                timeoutRetryRef.current = 1;
                setIsRetrying(true);
                const { id } = toast({
                    description: <Entity entity='table.error.timeout_retry' icon={ICONS.RETRY_LOADING} iconSize={16} />,
                    variant: 'warning',
                    duration: Infinity
                });
                retryToastIdRef.current = id;
                refetch?.();
            }
            // Case 2: API Gateway timeout after retry
            else if ((isTimeoutApiGateway && timeoutRetryRef.current === 1) || isTimeoutALB) {
                timeoutRetryRef.current = 0;
                retryGaveUpRef.current = true;
                setIsRetrying(false);
                dismissRetryToast();
                if (hasExport) {
                    setShowModalConfirmExportAfterTimeout(true);
                }
                toast({
                    description: <Entity entity='table.error.timeout_after_retry' />,
                    variant: 'danger'
                });
            }
            // Case 3: Bad gateway
            else if (isBadGateway) {
                toast({
                    description: <Entity entity='table.error.bad_gateway' />,
                    variant: 'danger'
                });
            }
            // Case 4: Loading error
            else {
                toast({
                    description: <Entity entity='table.error.loading' />,
                    variant: 'danger'
                });
            }
        }

        prevIsFetchingRef.current = isFetching;
    }, [error, isFetching, refetch, hasExport, setShowModalConfirmExportAfterTimeout, isTimeoutApiGateway, isTimeoutALB, isBadGateway]);

    return isRetrying;
};

export default useTableTimeout;